import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Clock, Mail, LogOut, AlertTriangle, CreditCard, Lock, Phone, Download } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// During HARD phase (days 9-10), the user can only access these pages.
// Everything else redirects to /subscription.
const PATHS_ALLOWED_WHEN_HARD = [
  '/subscription',
  '/meus-dados',
  '/login',
];

// Bloqueio do dia 11: o sistema fica fechado, mas as três SAÍDAS continuam abertas.
// /subscription = assinar de novo; /meus-dados = exportar dados e excluir a conta.
// Sem isto, a pessoa fica presa numa tela sem ação nenhuma (era o comportamento antigo).
const PATHS_ALLOWED_WHEN_LOCKED_OUT = [
  '/subscription',
  '/meus-dados',
  '/login',
];

// During MODERATE phase (days 3-8), only these paths redirect to /subscription.
// All other pages remain accessible.
const PATHS_BLOCKED_WHEN_MODERATE = [
  '/send',
  '/campaigns',
  '/whatsapp',
];

// Assinatura cancelada pelo usuário: mantém acesso a tudo, EXCETO as telas de
// envio/campanhas/WhatsApp, que redirecionam para /subscription (reativar plano).
const PATHS_BLOCKED_WHEN_CANCELED = [
  '/send',
  '/campaigns',
  '/whatsapp',
];

/**
 * Wraps the authenticated app and enforces Trial + Payment gating:
 *  - Trial: countdown banner (≤ 3 days), expired → upgrade or sub-user block
 *  - Payment:
 *      soft       → yellow banner (Days 0-2)
 *      moderate   → red banner + 402 on send/campaign endpoints (Days 3-8)
 *      hard       → only /subscription and /my-data accessible (Days 9-10)
 *      locked_out → full-screen "Contact Watizap" (Days 11+)
 *  - Super admins bypass entirely.
 */
export default function TrialGuard({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  if (!user || user.role === 'SUPER_ADMIN') return children;

  const trial = user.trial || {};
  const payment = user.payment || {};
  const isTrialBlocked = !!user.trial_blocked;
  const canUpgrade = !!user.can_upgrade;
  const phase = payment.phase || 'ok';
  const isCanceled = !!user.subscription_canceled;

  // ----- LOCKED OUT (Day 11+) -----
  if (phase === 'locked_out') {
    // As três saídas continuam abertas; o resto do sistema fica fechado. A faixa vermelha
    // acompanha essas telas para o usuário não perder o contexto de que está bloqueado.
    if (PATHS_ALLOWED_WHEN_LOCKED_OUT.some((p) => location.pathname.startsWith(p))) {
      return (
        <>
          <div className="bg-red-600 text-white text-sm px-4 py-2 text-center">
            Conta bloqueada por falta de pagamento.
            {canUpgrade ? ' Assine novamente para voltar a usar o sistema.' : ''}
          </div>
          {children}
        </>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-lg w-full border-red-300" data-testid="payment-locked-out-screen">
          <CardContent className="pt-6 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2 text-red-700">
              Conta bloqueada
            </h2>
            <p className="text-muted-foreground mb-4">
              {payment.stripe_closed ? (
                <>
                  Sua assinatura foi cancelada por falta de pagamento e <strong>não há
                  cobranças pendentes</strong>. Seus dados continuam salvos.
                </>
              ) : (
                <>
                  Sua conta foi bloqueada por pendência financeira
                  {payment.days_past_due ? <> ({payment.days_past_due} dias em atraso)</> : null}.
                </>
              )}
            </p>

            {canUpgrade ? (
              <>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-left">
                  <p className="text-sm text-red-900">
                    Para voltar a usar o sistema, faça uma nova assinatura. Seus contatos,
                    grupos e histórico estão preservados. Após assinar, será necessário
                    conectar o WhatsApp novamente.
                  </p>
                </div>
                <Button
                  className="w-full mb-2"
                  onClick={() => navigate('/subscription')}
                  data-testid="payment-lockout-subscribe"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Assinar novamente
                </Button>
                <Button
                  variant="outline"
                  className="w-full mb-2"
                  onClick={() => navigate('/meus-dados')}
                  data-testid="payment-lockout-mydata"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Exportar meus dados ou excluir a conta
                </Button>
              </>
            ) : (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-red-900 mb-2 font-medium flex items-center gap-2">
                  <Phone className="w-4 h-4 text-red-700" />
                  Fale com o responsável pela conta
                </p>
                <p className="text-sm text-red-800">
                  Somente o titular pode contratar um novo plano
                  {user.tenant_owner_email ? <> ({user.tenant_owner_email})</> : null}.
                </p>
              </div>
            )}

            <Button
              variant="outline"
              className="w-full"
              onClick={logout}
              data-testid="payment-lockout-logout"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ----- Trial expired + sub-user → hard block screen -----
  if (isTrialBlocked && !canUpgrade) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mb-4">
              <Clock className="w-7 h-7 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2" data-testid="trial-expired-subuser-title">
              Período de teste encerrado
            </h2>
            <p className="text-muted-foreground mb-4">
              O período de teste da sua organização acabou
              {trial.trial_expires_at ? ' em ' + new Date(trial.trial_expires_at).toLocaleDateString('pt-BR') : ''}.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 text-left">
              <p className="text-sm text-amber-900 mb-1 font-medium flex items-center gap-2">
                <Mail className="w-4 h-4 text-amber-700" />
                Para continuar usando o Watizap:
              </p>
              <p className="text-sm text-amber-800">
                Peça ao administrador
                {user.tenant_owner_email ? (
                  <> <strong className="text-amber-900">{user.tenant_owner_email}</strong></>
                ) : null}
                {' '}para fazer upgrade do plano.
              </p>
            </div>
            <Button variant="outline" className="w-full" onClick={logout} data-testid="trial-expired-logout">
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ----- Payment HARD phase + sub-user → contact-admin screen -----
  if (phase === 'hard' && !canUpgrade) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <CreditCard className="w-7 h-7 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2" data-testid="payment-hard-subuser-title">
              Pagamento pendente
            </h2>
            <p className="text-muted-foreground mb-4">
              A assinatura desta organização está com pagamento pendente
              {payment.days_past_due ? <> há <strong>{payment.days_past_due} dias</strong></> : null}.
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-left">
              <p className="text-sm text-red-900 mb-1 font-medium flex items-center gap-2">
                <Mail className="w-4 h-4 text-red-700" />
                Para continuar usando o Watizap:
              </p>
              <p className="text-sm text-red-800">
                Peça ao administrador
                {user.tenant_owner_email ? (
                  <> <strong className="text-red-900">{user.tenant_owner_email}</strong></>
                ) : null}
                {' '}para regularizar o pagamento.
              </p>
            </div>
            <Button variant="outline" className="w-full" onClick={logout} data-testid="payment-hard-logout">
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ----- Trial expired + admin → force /subscription -----
  if (isTrialBlocked && canUpgrade && !PATHS_ALLOWED_WHEN_HARD.includes(location.pathname)) {
    return <Navigate to="/subscription" replace state={{ blocked: true }} />;
  }

  // ----- Payment HARD + admin → only /subscription and /my-data -----
  if (phase === 'hard' && canUpgrade && !PATHS_ALLOWED_WHEN_HARD.includes(location.pathname)) {
    return <Navigate to="/subscription" replace state={{ paymentHard: true }} />;
  }

  // ----- Payment MODERATE + admin → block /send and /campaigns -----
  if (
    phase === 'moderate' &&
    canUpgrade &&
    PATHS_BLOCKED_WHEN_MODERATE.some((p) => location.pathname.startsWith(p))
  ) {
    return <Navigate to="/subscription" replace state={{ paymentModerate: true }} />;
  }

  // ----- Payment MODERATE + sub-user trying to access /send or /campaigns -----
  // Sub-users can't open /subscription (ADMIN-only), so show a dedicated orange screen
  // explaining the situation and asking them to contact the admin.
  if (
    phase === 'moderate' &&
    !canUpgrade &&
    PATHS_BLOCKED_WHEN_MODERATE.some((p) => location.pathname.startsWith(p))
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full border-orange-300" data-testid="payment-moderate-subuser-screen">
          <CardContent className="pt-6 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center mb-4">
              <CreditCard className="w-7 h-7 text-orange-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2 text-orange-700" data-testid="payment-moderate-subuser-title">
              Pagamento pendente
            </h2>
            <p className="text-muted-foreground mb-4">
              A assinatura desta organização está com pagamento pendente
              {payment.days_past_due ? <> há <strong>{payment.days_past_due} dias</strong></> : null}.
              Por isso, o envio de mensagens, a criação de campanhas e a conexão do WhatsApp estão temporariamente bloqueados.
            </p>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4 text-left">
              <p className="text-sm text-orange-900 mb-1 font-medium flex items-center gap-2">
                <Mail className="w-4 h-4 text-orange-700" />
                Para liberar o envio, as campanhas e o WhatsApp:
              </p>
              <p className="text-sm text-orange-800">
                Peça ao administrador
                {user.tenant_owner_email ? (
                  <> <strong className="text-orange-900">{user.tenant_owner_email}</strong></>
                ) : null}
                {' '}para regularizar o pagamento.
              </p>
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => window.history.back()}
              data-testid="payment-moderate-back"
            >
              Voltar
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ----- Subscription canceled + admin → bloqueia envio/campanhas/whatsapp -----
  if (
    isCanceled &&
    canUpgrade &&
    PATHS_BLOCKED_WHEN_CANCELED.some((p) => location.pathname.startsWith(p))
  ) {
    return <Navigate to="/subscription" replace state={{ canceled: true }} />;
  }

  // ----- Subscription canceled + sub-user → tela "fale com o admin" -----
  if (
    isCanceled &&
    !canUpgrade &&
    PATHS_BLOCKED_WHEN_CANCELED.some((p) => location.pathname.startsWith(p))
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full border-orange-300" data-testid="canceled-subuser-screen">
          <CardContent className="pt-6 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center mb-4">
              <CreditCard className="w-7 h-7 text-orange-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2 text-orange-700" data-testid="canceled-subuser-title">
              Assinatura cancelada
            </h2>
            <p className="text-muted-foreground mb-4">
              A assinatura desta organização foi cancelada. Por isso, o envio de mensagens,
              as campanhas e a conexão do WhatsApp estão indisponíveis.
            </p>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4 text-left">
              <p className="text-sm text-orange-900 mb-1 font-medium flex items-center gap-2">
                <Mail className="w-4 h-4 text-orange-700" />
                Para voltar a usar:
              </p>
              <p className="text-sm text-orange-800">
                Peça ao administrador
                {user.tenant_owner_email ? (
                  <> <strong className="text-orange-900">{user.tenant_owner_email}</strong></>
                ) : null}
                {' '}para reativar um plano.
              </p>
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => window.history.back()}
              data-testid="canceled-subuser-back"
            >
              Voltar
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ----- Banners (active app states) -----
  const showTrialCountdown =
    trial.is_trial && !isTrialBlocked &&
    typeof trial.days_remaining === 'number' && trial.days_remaining <= 3;

  const showPaymentSoftBanner = phase === 'soft';
  const showPaymentModerateBanner = phase === 'moderate';
  const showPaymentHardBanner = phase === 'hard';  // only visible on /subscription + /my-data

  return (
    <>
      {showTrialCountdown && (
        <div
          className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-sm text-amber-900 flex items-center justify-center gap-2"
          data-testid="trial-countdown-banner"
        >
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <span>
            {trial.days_remaining === 0 ? (
              <>Seu período de teste expira <strong>hoje</strong>.</>
            ) : trial.days_remaining === 1 ? (
              <>Seu período de teste expira em <strong>1 dia</strong>.</>
            ) : (
              <>Seu período de teste expira em <strong>{trial.days_remaining} dias</strong>.</>
            )}
            {canUpgrade && (
              <>
                {' '}
                <a href="/subscription" className="underline font-semibold hover:text-amber-700">
                  Fazer upgrade agora
                </a>
              </>
            )}
          </span>
        </div>
      )}

      {showPaymentSoftBanner && (
        <div
          className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-sm text-amber-900 flex items-center justify-center gap-2"
          data-testid="payment-soft-banner"
        >
          <CreditCard className="w-4 h-4 text-amber-600" />
          <span>
            Houve uma falha no pagamento da sua assinatura
            {payment.days_past_due ? <> ({payment.days_past_due} {payment.days_past_due === 1 ? 'dia' : 'dias'})</> : null}.
            {canUpgrade ? (
              <>
                {' '}
                <a href="/subscription" className="underline font-semibold hover:text-amber-700">
                  Atualize seu meio de pagamento
                </a>{' '}
                para evitar interrupções.
              </>
            ) : (
              <> Avise o administrador para regularizar.</>
            )}
          </span>
        </div>
      )}

      {showPaymentModerateBanner && (
        <div
          className="bg-orange-50 border-b border-orange-300 px-4 py-2 text-sm text-orange-900 flex items-center justify-center gap-2"
          data-testid="payment-moderate-banner"
        >
          <CreditCard className="w-4 h-4 text-orange-600" />
          <span>
            Pagamento pendente há <strong>{payment.days_past_due} dias</strong>.
            Envio de mensagens, campanhas e conexão do WhatsApp estão bloqueados.
            {canUpgrade && (
              <>
                {' '}
                <a href="/subscription" className="underline font-semibold hover:text-orange-700">
                  Regularizar agora
                </a>
              </>
            )}
          </span>
        </div>
      )}

      {showPaymentHardBanner && (
        <div
          className="bg-red-100 border-b border-red-400 px-4 py-2 text-sm text-red-900 flex items-center justify-center gap-2"
          data-testid="payment-hard-banner"
        >
          <Lock className="w-4 h-4 text-red-700" />
          <span>
            Acesso restrito: apenas <strong>Minha Assinatura</strong> e <strong>Meus Dados</strong>{' '}
            estão disponíveis até a regularização do pagamento ({payment.days_past_due} dias).
          </span>
        </div>
      )}

      {isCanceled && canUpgrade && (
        <div
          className="bg-red-50 border-b border-red-300 px-4 py-2 text-sm text-red-900 flex items-center justify-center gap-2"
          data-testid="canceled-banner"
        >
          <CreditCard className="w-4 h-4 text-red-600" />
          <span>
            Sua assinatura foi cancelada. Envio, campanhas e WhatsApp estão bloqueados.{' '}
            <a href="/subscription" className="underline font-semibold hover:text-red-700">
              Reativar um plano
            </a>
          </span>
        </div>
      )}

      {children}
    </>
  );
}
